import React from 'react';

import { CheckboxList } from './CheckboxList';
import type { ListStatus } from '../hooks/useInfiniteList';

export interface PickableTag {
  id: string;
  name: string;
  slug: string;
}

interface TagPickerProps {
  tags: PickableTag[];
  /** Selected tag UUIDs. */
  selectedIds: string[];
  onToggle: (tagId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** The group's legend, e.g. "Event types". */
  legend: string;
  /** Under the legend, linked to the group. */
  help?: string;
  emptyMessage?: string;
  className?: string;
  /*
   * An unbounded set loads in batches (ruling 21). Pass these from the
   * picker's useInfiniteList: a server search above the options, and the
   * shared trailing controls (Load more, Retry, …) inside the scroller.
   */
  searchInput?: string;
  onSearchChange?: (value: string) => void;
  /** The applied search, for the no-match copy. */
  search?: string;
  listStatus?: ListStatus;
  hasMore?: boolean;
  onLoadMore?: (options?: { auto?: boolean }) => void;
  onRetryBatch?: () => void;
  onReset?: () => void;
  totalCount?: number | null;
  /** A failed first load or reset: rendered inside the group, with Retry. */
  loadError?: unknown;
  loadErrorTitle?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * Multi-select over an organization's event types (tags), keyed by UUID — the
 * kit CheckboxList with event-type copy.
 *
 * A picker rather than a text field on purpose: `supportedTags` takes tag
 * UUIDs, those UUIDs appear nowhere in the portal, and the publisher
 * credential that would otherwise resolve them lacks `tags:read`. Typing one
 * by hand is not a thing anyone can reasonably do, and getting it wrong is the
 * platform's most common silent failure — the version registers fine and then
 * matches no subscription.
 */
export const TagPicker: React.FC<TagPickerProps> = ({
  tags,
  selectedIds,
  onToggle,
  isLoading = false,
  disabled = false,
  legend,
  help,
  emptyMessage = 'No event types are defined for this organization yet.',
  className,
  searchInput,
  onSearchChange,
  search,
  listStatus,
  hasMore,
  onLoadMore,
  onRetryBatch,
  onReset,
  totalCount,
  loadError,
  loadErrorTitle = "Couldn't load event types",
  onRetry,
  isRetrying,
}) => {
  const selected = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  return (
    <CheckboxList
      legend={legend}
      description={help}
      items={tags.map((tag) => ({ id: tag.id, label: tag.name, meta: tag.slug }))}
      selectedIds={selected}
      onToggle={onToggle}
      disabled={disabled}
      itemNoun="event types"
      searchPlaceholder="Search event types…"
      idPrefix="tag-option"
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      className={className}
      searchInput={searchInput}
      onSearchChange={onSearchChange}
      search={search}
      listStatus={listStatus}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onRetryBatch={onRetryBatch}
      onReset={onReset}
      totalCount={totalCount}
      loadedCount={tags.length}
      loadError={loadError}
      loadErrorTitle={loadErrorTitle}
      onRetry={onRetry}
      isRetrying={isRetrying}
    />
  );
};
